import { computed, onMounted } from 'vue'
import useGet from '../../../core/composables/api/useGet'
import { useEventBus } from '@vueuse/core'
import { EventKey } from '../../../core/events/event-key'
import type { Member } from '@/core/models/member/member'
import { OpenFeedbackState } from '@/modules/open-feedbacks/composables/useOpenFeedbacks'
import type { SurveyRecurrenceFrequency } from '@/modules/start-survey/composables/use-start-survey'

export const useFeedbackTasks = () => {
  const { data, status, load, reload } = useGet<FeedbackTask[]>('task')

  onMounted(() => {
    load()
  })

  const feedbackTasks = computed(() => {
    return data.value?.filter(
      (task) =>
        task.surveyTandems.every((tandem) => tandem.senderState !== OpenFeedbackState.Completed) &&
        task.surveyId,
    )
  })

  const recurringTasks = computed(() => {
    return data.value
      ?.filter(
        (task) =>
          task.nextOccurrenceAt &&
          task.origin === 'coach' &&
          (task.surveyTandems.every(
            (tandem) => tandem.senderState === OpenFeedbackState.Completed,
          ) ||
            !task.surveyId),
      )
      .sort(
        (a, b) =>
          new Date(a.recurrence?.nextOccurrenceAt ?? '').getTime() -
          new Date(b.recurrence?.nextOccurrenceAt ?? '').getTime(),
      )
  })

  const taskBus = useEventBus(EventKey.taskUpdated)
  taskBus.on(() => reload())
  const openFeedbackBus = useEventBus(EventKey.openFeedbackUpdated)
  openFeedbackBus.on(() => reload())

  return { feedbackTasks, recurringTasks, status }
}

export interface FeedbackTask {
  id: string
  nextOccurrenceAt: string
  title?: string
  message?: JSON
  owner: Member
  createdAt: string
  origin: 'personal' | 'coach' | 'assessment'
  numberOfQuestions: number
  timeEstimate: number
  isSelfFeedbackOnly: boolean
  surveyId: string
  feedbackTopics: ReadonlyArray<{
    id: string
    title: string
    icon?: string
    cta?: string
  }>
  recurrence?: {
    readonly frequency: SurveyRecurrenceFrequency
    readonly interval: number
    readonly occurrenceCount: number
    readonly nextOccurrenceAt: string
  }
  surveyTandems: ReadonlyArray<{
    senderState?: OpenFeedbackState
    receiver: Member
  }>
}

export interface FeedbackRequest {
  readonly surveyId: string
}
